import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Form, Input, notification,
} from 'antd';
import { DoubleLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import * as Auth from 'aws-amplify/auth';

import { generateNotificationFromCognitoError } from '../../utils/errorHelpers';
import VerificationForm from './verificationForm';
import { attributeMap, getValidationRule } from './util';
import { updateState, verificationState } from './state';

const UserAttributeForm = (props) => {
  const {
    attributeName,
    goBack,
    onUpdate,
    user,
  } = props;
  const currentValue = user[attributeName];
  const { t } = useTranslation();
  const [state, setState] = useState(updateState.NOT_UPDATED);
  const attributeText = t(`common.user.${attributeName}`);
  const [form] = Form.useForm();

  const handleOnFinish = async (fields) => {
    const key = attributeMap[attributeName];
    const value = fields[attributeName];
    if (value === currentValue) {
      notification.info({
        message: t('settings.userInformation.change.info.noChange', { value: attributeText }),
      });
      return;
    }

    try {
      setState(updateState.UPDATE_REQUESTED);
      await Auth.updateUserAttributes({ userAttributes: { [key]: value } });
      if (attributeName === 'phone') {
        // Phone number change doesn't get verified automatically
        await Auth.sendUserAttributeVerificationCode({
          userAttributeKey: 'phone_number',
        });
      }
      setState(updateState.NOT_VERIFIED);
    } catch (err) {
      setState(updateState.NOT_UPDATED);
      notification.error(generateNotificationFromCognitoError(t, err));
    }
  };

  const isUpdated = Object.keys(verificationState).includes(state);

  const handleClick = () => {
    if (isUpdated) {
      onUpdate();
    }
    goBack();
  };

  const rules = [
    {
      ...getValidationRule(attributeName),
      message: t('settings.userInformation.change.error.invalid'),
    },
    {
      required: true,
      message: t('settings.userInformation.change.error.required'),
    },
  ];

  return (
    <>
      <h2>
        {t('settings.userInformation.change.title', {
          value: attributeText.toLowerCase(),
        })}
      </h2>
      <Form name="userAttributeForm" onFinish={handleOnFinish} form={form}>
        <Form.Item
          label={t('settings.userInformation.change.label', {
            value: attributeText.toLowerCase(),
          })}
          name={attributeName}
          initialValue={currentValue}
          rules={rules}
        >
          <Input placeholder={attributeText} disabled={isUpdated} />
        </Form.Item>
        <Form.Item shouldUpdate>
          {() => (
            <Button
              type="primary"
              htmlType="submit"
              loading={state === updateState.UPDATE_REQUESTED}
              disabled={
                form.getFieldsError().some(({ errors }) => errors.length > 0)
                || state !== updateState.NOT_UPDATED
              }
            >
              {t('common.button.submit')}
            </Button>
          )}
        </Form.Item>
      </Form>
      {isUpdated && (
        <VerificationForm
          attribute={attributeMap[attributeName]}
          attributeText={attributeText}
          state={state}
          setState={setState}
        />
      )}
      <Button type="link" className="link" onClick={handleClick}>
        <DoubleLeftOutlined />
        {t('common.button.back')}
      </Button>
    </>
  );
};

UserAttributeForm.displayName = 'UserAttributeForm';
UserAttributeForm.propTypes = {
  attributeName: PropTypes.oneOf(['email', 'phone']).isRequired,
  goBack: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  user: PropTypes.shape({
    email: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
};

export default UserAttributeForm;
